import { FaInfoCircle } from '@meronex/icons/fa';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import useContext from '../../../../../hooks/useContext';
import {
  TApiaDataGridField,
  TDataColumn,
} from '../../../../../store/dataGridSlice/types';
import { makeCellFieldId } from '../../../../../store/dataGridSlice/util';
import usePropsSelector from '../../../../../store/propsStore/usePropsSelector';
import useDomProps from '../../../../../store/selection/useDomProps';
import useOtherTagButton from '../../../../common/buttons/useOtherTagButton';
import { uploaders } from '../../../../common/uploader/UploaderApi';
import makeDocTypeTooltip from '../../../common/modals/documentUploadModal/makeDocTypeTooltip';
import fieldsStore from '../../fieldsStore/fieldsStore';
import { DataGridIdentifier } from './context';
import RequiredBox from '../../common/RequiredBox';
import { useApiaFormContext } from '../../page/ApiaForm';
import IProperty from '../../../../../JSApi/constants/IProperty';
import { Icon } from '@apia/icons';
import { ApiaUtil } from '@apia/components';
import { isTrue } from '../../ApiaComponent';
import { arrayOrArray } from '@apia/util';

const HeaderCell = ({
  columnIndex,
  column,
}: {
  columnIndex: number;
  column: TDataColumn;
}) => {
  const { gridId } = useContext(DataGridIdentifier);

  const { showInfoButton } = useAppSelector((global) => {
    const state = global.dataGridSlice[gridId];
    return {
      showInfoButton: !isTrue(
        state.context.grid.field.properties.hideDocInfoButton,
      ),
    };
  });
  const cellId = React.useMemo(
    () => makeCellFieldId(gridId, columnIndex, 0),
    [columnIndex, gridId],
  );

  const { defaultWidth, isColumnRequired } = useAppSelector((global) => {
    const state = global.dataGridSlice[gridId];
    const grid = state.context.grid.field;

    const w = arrayOrArray(grid.gridHeader.col)[columnIndex]?.colWidth;

    return {
      defaultWidth: w,
      isColumnRequired: state.requiredColumns.includes(columnIndex),
    };
  });

  const cellProps = usePropsSelector(
    cellId,
    (notTyped) => {
      const current = notTyped as unknown as TApiaDataGridField;
      return {
        isRequired: current?.properties?.required,
        columnWidth: current?.properties?.colWidth,
        fieldType: current?.fieldType,
        isHidden: current?.fieldType === 'hidden',
        showAsText: current?.properties?.[IProperty.PROPERTY_INPUT_AS_TEXT],
        attId: current?.attId,
      };
    },
    shallowEqual,
  );

  const minWidth = React.useMemo(
    () => (cellProps?.fieldType === 'check' ? 100 : 250),
    [cellProps?.fieldType],
  );

  const domProps = useDomProps(
    'th',
    {
      columnIndex,
    },
    (global) => global.dataGridSlice[gridId],
  );

  const isStoreHidden = useAppSelector(
    (global) => global.dataGridSlice[gridId].columns[columnIndex].isHidden,
  );

  const buttonProps = useOtherTagButton(
    React.useMemo(
      () => ({
        className: 'grid__header__cell',
        onClick: () =>
          fieldsStore.getById(gridId).fireEvent('gridColumnSelect'),
      }),
      [gridId],
    ),
  );

  const { isFormReadOnly } = useApiaFormContext();

  const nodeLabel = (
    <Box
      as="span"
      {...(!column.label
        ? {
            dangerouslySetInnerHTML: {
              __html: '&nbsp;',
            },
          }
        : undefined)}
    >
      {column.label ? column.label : undefined}
    </Box>
  );

  const actualWidth = cellProps?.columnWidth ?? defaultWidth;

  if (cellProps?.isHidden || isStoreHidden) return null;
  if (column.isHidden) return null;
  return (
    <th
      {...domProps}
      {...buttonProps}
      sx={{
        maxWidth: `${actualWidth ? Number(actualWidth) : minWidth}px`,
        minWidth: `${actualWidth ? Number(actualWidth) : minWidth}px`,
        width: `${actualWidth ? Number(actualWidth) : minWidth}px`,
      }}
      role={undefined}
      data-attname={column.attName}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <RequiredBox
          labelNode={nodeLabel}
          required={!!cellProps?.attId && isColumnRequired}
          avoidColon
          isFormReadonly={isFormReadOnly || cellProps?.showAsText}
        />
        {cellProps?.fieldType === 'file' && showInfoButton && (
          <Icon
            {...window.getVariant('layout.common.components.infoButton')}
            icon={FaInfoCircle}
            aria-label={window.LBL_SUPPORTED_DOCTYPES}
            title={''}
            size="Sm"
            onMouseEnter={(ev) => {
              ApiaUtil.instance.tooltips.open({
                attachToElement: () => ev.target as HTMLElement,
                children: makeDocTypeTooltip(uploaders[cellId]),
              });
            }}
          />
        )}
      </Box>
    </th>
  );
};
export default HeaderCell;
